import React, { useState } from "react";
import { Box, Button, Input, Stack } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import mixpanel from "mixpanel-browser";
import BackButton from "components/common/BackButton";
import CustomTypography from "components/common/CustomTypography";
import { clearCoachBrowser } from "api/coachAuth";
import { type CustomResponseFormat, setAuthHeaders } from "api";
import { userApi } from "api/userApi";
import { userAtom, type UserType } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { theme } from "styles/theme";
import distancingLogo from "static/image/common/distancing_logo.png";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// 독립 로그인 페이지 입력 표준 (AmbassadorLogin / PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

/**
 * 스토어 심사용 이메일 로그인.
 *
 * 일반 사용자 동선에는 없다 — iOS 앱 로그인 화면에서만 진입한다.
 * 자격증명은 서버(review.login.*)가 갖고 있고 여기서는 형식만 본다. 예전에는 계정과 비밀번호가
 * 이 파일에 하드코딩되어 공개 번들에 그대로 실렸다.
 */
export default function EmailLoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setUser = useSetRecoilState(userAtom);
  const setPatient = useSetRecoilState(patientAtom);

  const emailMalformed = email.length > 0 && !isValidEmail(email.trim());
  const canSubmit =
    isValidEmail(email.trim()) && password.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // 서버는 "이메일:비밀번호" 를 type=other 로 받아 설정된 심사 계정과 대조한다.
      const idToken = `${email.trim()}:${password}`;
      const res = (await userApi.login({
        type: "other",
        idToken,
      })) as unknown as CustomResponseFormat<UserType> & { result?: any };

      const result: any = (res as any)?.result;
      if (!result?.accessToken) {
        setError("아이디 또는 비밀번호를 확인해 주세요.");
        return;
      }

      if (result?.trackId) {
        mixpanel.identify(result.trackId);
      }
      setAuthHeaders(result.accessToken);
      clearCoachBrowser(); // 유저 로그인 — 코치 브라우저 표식 제거

      setUser(result as UserType);
      if (result?.role === "patient" && result?.userId) {
        setPatient({
          patientId: Number(`${result.userId}`),
          nickname: result.nickname,
        });
      }
      navigate("/", { replace: true });
    } catch (e) {
      setError("아이디 또는 비밀번호를 확인해 주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level3,
        pt: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* 절대 위치는 안전영역 패딩 위에 얹히므로 top 에 직접 더한다 (노치 기기 상태바 겹침 방지) */}
      <Box
        sx={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          left: "8px",
          zIndex: 1,
        }}
      >
        <BackButton onBack={() => navigate(-1)} />
      </Box>

      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "400px",
          px: "20px",
          py: "64px",
        }}
      >
        <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
          <img
            src={distancingLogo}
            alt="distancing"
            style={{ width: "138px", height: "auto" }}
          />

          <Stack spacing={"12px"} alignItems="center" sx={{ width: "100%" }}>
            <CustomTypography
              variant="H3"
              sx={{
                textAlign: "center",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {"이메일로 로그인"}
            </CustomTypography>
            <CustomTypography
              variant="Body"
              sx={{
                textAlign: "center",
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              {"이메일과 비밀번호를 입력해 주세요."}
            </CustomTypography>
          </Stack>

          {/* 입력 그룹과 CTA를 한 묶음으로 — 사이 간격은 8px (문구 슬롯이 여백 역할) */}
          <Stack spacing={"8px"} sx={{ width: "100%" }}>
            <Stack spacing={"12px"} sx={{ width: "100%" }}>
              <Input
                sx={inputSx}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                placeholder="이메일"
                slotProps={{
                  input: {
                    inputMode: "email",
                    autoComplete: "username",
                    "aria-label": "이메일",
                  },
                }}
              />
              <Input
                sx={inputSx}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                placeholder="비밀번호"
                slotProps={{
                  input: {
                    autoComplete: "current-password",
                    "aria-label": "비밀번호",
                  },
                }}
              />

              {/* 안내/에러 고정 슬롯 — 문구 유무와 무관하게 높이를 유지해 CTA가 밀리지 않게 한다 */}
              <CustomTypography
                variant="Caption"
                role="alert"
                sx={{
                  // 인풋 스택 기본 간격(12px)보다 인풋에 더 붙인다 — Stack spacing을 이기려면 !important 필요
                  marginTop: "4px !important",
                  minHeight: "20px",
                  textAlign: "left",
                  fontWeight: 500,
                  color: theme.vars.palette.Content.HighlightWithAlert,
                }}
              >
                {emailMalformed
                  ? "올바른 이메일 형식이 아니에요."
                  : error || ""}
              </CustomTypography>
            </Stack>

            <Button
              variant="Solid"
              size="lg"
              loading={submitting}
              disabled={!canSubmit}
              onClick={handleSubmit}
              sx={{
                width: "100%",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"로그인"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
